import type { CustomerEntity, UserEntity, UserRoleEntity } from '@solinkcorp/api-types';
import settings from '@solinkcorp/settings';
import { USER_TYPE_ADMIN, USER_TYPE_STANDARD } from '../authConstants';
import { type CallHomeAuthorizeResult, type UserPreferences } from './callHomeAuthTypes';
import { CACHED_AUTHORIZE_KEY, localhostAuthCache } from './localhostAuthCache';

/**
 * Authorize using the provided id token
 * @param idToken The token we will use to authorize with
 */
export async function authorize(
  idToken: string,
  snowflakeToken?: string,
  bypassCache?: boolean,
): Promise<CallHomeAuthorizeResult> {
  const cached = localhostAuthCache(CACHED_AUTHORIZE_KEY, undefined, bypassCache);
  if (cached) return cached;

  const response = await fetch(`${settings.callhome_url}/api/users/authorize`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    // includeBiAccess: true triggers Snowflake user provisioning on CHS.
    // CHS gates this server-side with the dataExplorer feature flag, so
    // passing true is safe. CHS skips provisioning when dataExplorer is
    // not enabled for the user's role, customer, reseller, or cloud.
    // snowflakeToken: when provided, CHS echoes it back as a Set-Cookie so the
    // browser stores it as a server-set backup alongside the JS cookie.
    body: JSON.stringify({
      includeBiAccess: true,
      ...(snowflakeToken && { snowflakeToken }),
    }),
  });

  if (!response.ok) {
    const res = await response.json();
    throw new Error(res.error.statusCode + ': ' + res.error.message);
  }

  const result = await response.json();
  localhostAuthCache(CACHED_AUTHORIZE_KEY, result);
  return result;
}

/**
 * Impersonate the user with the specified userId
 *
 * @param userId - The userId to impersonate.
 * @param authToken - The token to for the user doing the impersonating.
 */
export async function impersonateUser(
  authToken: string,
  userId: string,
  reason?: string,
  bypassCache?: boolean,
): Promise<CallHomeAuthorizeResult> {
  const cacheKey = `CACHED_IMPERSONATE_${userId}`;
  const cached = localhostAuthCache(cacheKey, undefined, bypassCache);
  if (cached) return cached;

  const response = await fetch(`${settings.callhome_url}/api/users/${userId}/impersonate`, {
    method: 'POST',
    // See authorize() for why includeBiAccess is always true.
    body: JSON.stringify({
      ...(reason && { metadata: { reason } }),
      includeBiAccess: true,
    }),
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (response.status === 404) {
    throw new Error('User not found.');
  } else if (response.status === 401 || response.status === 403) {
    throw new Error('Unable to impersonate specified user.');
  } else if (!response.ok) {
    throw new Error(`Unexpected reply: ${response.status}`);
  }

  const result = await response.json();
  localhostAuthCache(cacheKey, result);
  return result;
}

export async function getUserRoles(
  user: {
    accessToken: string;
    customerId: string;
  },
  options?: {
    limit?: number;
    skip?: number;
    order?: string;
  },
): Promise<UserRoleEntity[]> {
  const where: any = {
    and: [
      { customerId: user.customerId },
      {
        userType: {
          inq: [USER_TYPE_ADMIN, USER_TYPE_STANDARD],
        },
      },
    ],
  };

  return await callHomeGet(user.accessToken, '/api/userroles', {
    filter: JSON.stringify({
      limit: options?.limit,
      skip: options?.skip,
      order: options?.order || 'name ASC',
      where,
    }),
  });
}

export async function getCustomers(
  accessToken: string,
  params: {
    search?: string;
    limit?: number;
    impersonatableCustomerIds?: string[];
  } = {},
): Promise<CustomerEntity[]> {
  const filter: any = {
    order: 'name ASC',
    limit: params.limit,
    where: { and: [] },
  };

  if (params.impersonatableCustomerIds?.length) {
    filter.where.and.push({
      id: {
        inq: params.impersonatableCustomerIds,
      },
    });
  }

  if (params.search) {
    const ilike = `%${params.search}%`;
    filter.where.and.push({ or: [{ id: { ilike } }, { name: { ilike } }] });
  }

  return await callHomeGet(accessToken, '/api/customers', { filter: JSON.stringify(filter) });
}

/**
 * Retrieve users from Callhome.
 *
 * @param [params.search] - String to search for.
 * @param [params.customerId] - The customer to search for users.  If you are
 *   impersonating, you can leave this undefined to search across customers.
 * @param [params.limit] - The maximum number of users to retrieve.
 * @param [params.canImpersonate] - If true, only users that can be impersonated
 *   will be returned.
 */
export async function getUsers(
  accessToken: string,
  params: {
    search?: string;
    customerId?: string;
    limit?: number;
    canImpersonate?: boolean;
    impersonatableUserIds?: string[];
  },
): Promise<UserEntity[]> {
  const where: any = {
    and: [
      {
        userType: {
          inq: [USER_TYPE_ADMIN, USER_TYPE_STANDARD],
        },
      },
    ],
  };

  if (params.impersonatableUserIds?.length) {
    where.and.push({
      id: {
        inq: params.impersonatableUserIds,
      },
    });
  }

  if (params.customerId) {
    where.and.push({ customerId: params.customerId });
  }

  if (params.canImpersonate) {
    where.and.push({ isSupportAccessEnabled: true });
    where.and.push({
      or: [
        { supportAccessExpiresAt: { gt: Math.floor(Date.now() / 1000) } },
        { supportAccessExpiresAt: -1 },
      ],
    });
  }

  if (params.search) {
    const ilike = `%${params.search}%`;
    where.and.push({ or: [{ id: { ilike } }, { name: { ilike } }, { email: { ilike } }] });
  }

  return await callHomeGet(accessToken, '/api/users', {
    filter: JSON.stringify({
      limit: params.limit,
      order: 'email ASC',
      where,
      include: [
        {
          relation: 'customer',
          scope: ['id', 'name'],
        },
      ],
    }),
  });
}

export async function updateUserPreferences(
  accessToken: string,
  data: Partial<UserPreferences>,
): Promise<UserPreferences> {
  const response = await fetch(`${settings.callhome_url}/api/users/current/preferences`, {
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Unexpected reply: ${response.status}`);
  }

  return await response.json();
}

async function callHomeGet(accessToken: string, path: string, params?: Record<string, string>) {
  let search = '';
  if (params) {
    const searchParams = new URLSearchParams(params);
    search = `?${searchParams.toString()}`;
  }

  const response = await fetch(`${settings.callhome_url}${path}${search}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Unexpected reply: ${response.status}`);
  }

  return await response.json();
}

export async function getUserRole(accessToken: string, id: string): Promise<UserRoleEntity> {
  return await callHomeGet(accessToken, `/api/userroles/${id}`);
}
